/**
 * ADR 0401 §2 · ADR 0409 D3/D4 — กติกาสถานะของสินค้าในใบงาน · **ต้นฉบับฝั่งเบราว์เซอร์**
 * plan: 4-Archives/tms/deeptrack-plan/plan-to-freight-production/0409-driver-cargo-and-evidence.md
 *
 * API โหลดแพ็กเกจนี้ตอนรันไม่ได้ (ADR 0150) จึงเก็บสำเนาไว้ที่
 * `apps/api/src/modules/cargo/lib/cargo-item-status.ts` · ทั้งสองฝั่งปักกับ
 * `cargo-item.cases.json` ด้วยเทสของตัวเอง ⇒ ดริฟต์แล้วแดง
 *
 * ทุกที่ที่กติกานี้มีผล (ต้องอัปรายการนี้ด้วยเมื่อเพิ่มที่ใช้):
 * - api   `cargo-items.service` (create · update · status · bulk-status · restore)
 * - api   `driver-cargo.service` (ADR 0409 Phase B — เรียก service ตัวเดียวกัน)
 * - web   `lib/cargo/cargo-item-rules.ts` **re-export จากไฟล์นี้ ไม่ได้นิยามซ้ำ**
 * - app   `apps/driver` จอหน้าดำ → `components/cargo/*` (ADR 0409 B4)
 * - web   `components/cargo/CargoStatusDialog.tsx` · `lib/cargo/status-needs-dialog.ts` (ADR 0454 R3 — "อื่น ๆ" ต้องระบุ)
 * - api   `lib/cargo-photo-keep.ts` · `setReturnPhotos` + app `CargoOutcomeSheet` วางคืน (ADR 0459 — `returnNeedsNewPhoto` · `CARGO_PHOTO_SETS`)
 *
 * เหตุผลที่ย้ายมาอยู่ที่นี่ (2026-10-05): ก่อนหน้านี้มีสองสำเนา (api + web) · พอแอปคนขับ
 * ต้องใช้ด้วย ทางเลือกคือสำเนาที่สาม หรือย้ายมาที่แพ็กเกจกลางให้สองแอปเบราว์เซอร์ใช้ร่วมกัน
 * — เลือกอย่างหลัง เพราะ "หนึ่งกติกา หนึ่งที่" สำคัญกว่าความสะดวกตอนย้าย
 */
import { cargoTicketLock, type CargoLockReason } from './cargo-ticket-lock'; // ADR 0467
export const CARGO_ACTIONS = ['pickup', 'deliver', 'return'] as const;
export type CargoAction = (typeof CARGO_ACTIONS)[number];

export const CARGO_ITEM_STATUSES = ['pending', 'done', 'partial', 'failed', 'cancelled'] as const;
export type CargoItemStatus = (typeof CARGO_ITEM_STATUSES)[number];

export const CARGO_STATUS_REQUIRED: Record<CargoItemStatus, ReadonlyArray<'qty_done' | 'reason_code' | 'reason_text'>> = {
  pending: [],
  done: [],
  partial: ['qty_done'],
  failed: ['reason_code'],
  cancelled: ['reason_text'],
};

/** มีแต่แถว pending ที่แก้จุด / จำนวน / ประเภท / โน้ตได้ (ADR 0401 §5). */
export const CARGO_EDITABLE_STATUSES: ReadonlyArray<CargoItemStatus> = ['pending'];

/** "จบที่จุดนั้นแล้ว" — ต่างจาก pending (ยังไม่ทำ) และ cancelled (ไม่ต้องทำแล้ว). */
export const CARGO_OUTCOMES: ReadonlyArray<CargoItemStatus> = ['done', 'partial', 'failed'];

/**
 * ADR 0409 D4 → **ADR 0415 D1** — ของที่ส่งไม่สำเร็จจบที่ไหน · enum ตายตัว **ไม่ใช่ master data**
 *   `return_to_stop` = เอาไปวางคืนที่หมุดหนึ่งของใบงานนี้ (`return_stop_id`)
 *   `lost`           = ของหาย/ถูกขโมย ไม่มีของให้วางคืน
 * ทั้งสองค่าเป็น **จุดจบ** — ชุดเดิม `on_truck` (ติดรถกลับ) ถูกตัดเพราะเป็นสภาพค้างกลางทาง ไม่ใช่ที่วางของ
 *   (เจ้าของ 2026-10-06: *"ทำได้แค่บอกว่าเอาของกลับไปวางที่ไหน แล้วจะได้จบใบงานนี้ไปได้"*)
 * เพราะเป็นข้อเท็จจริงที่โค้ดต้องอ่านไปตัดสินใจต่อ (ประตูจบงานของคนขับอ่านค่านี้ว่าต้องถ่ายรูปวางคืนไหม — 0415 D5)
 * ⚠️ ไม่มี "ของติดรถไหลไปใบงานพรุ่งนี้" — 0413 เคยเสนอ เจ้าของปฏิเสธ 2026-10-06 (ต้องมีจุดจบบนใบงานนี้)
 * เกณฑ์ออกจากแบบนี้: วันที่ต้องมีค่าที่ห้าและมันต่างกันต่อ org → ย้ายเป็น master data พร้อมฟิลด์
 * `behaviour` ที่โค้ดอ่าน **ไม่ใช่**เพิ่มค่าที่ห้าลง enum เงียบ ๆ
 */
export const CARGO_FAIL_DISPOSITIONS = ['return_to_stop', 'lost'] as const;
export type CargoFailDisposition = (typeof CARGO_FAIL_DISPOSITIONS)[number];

/** ใครเป็นคนตั้งสถานะ — คนละที่ยืน คนละสิ่งที่หยิบได้ (ดู `cargoStatusNeeds`). */
export const CARGO_STATUS_VIA = ['admin', 'driver'] as const;
export type CargoStatusVia = (typeof CARGO_STATUS_VIA)[number];

/**
 * ADR 0409 D3 — เพดานรูปต่อหนึ่งรายการ · **ADR 0454 R2: 100** (คนขับไม่เห็นตัวเลข — เพดานกันพังฝั่ง server เท่านั้น)
 * plan: 4-Archives/plan/tms/0454-driver-cargo-checklist-rows-and-form-rules.md
 */
export const CARGO_PHOTO_MAX = 100;

export type TransitionResult =
  | { ok: true }
  | { ok: false; reason: 'SAME_STATUS' | 'BAD_TRANSITION' | CargoLockReason };

export function canTransition(from: CargoItemStatus, to: CargoItemStatus, ticketStatus: string): TransitionResult {
  if (from === to) return { ok: false, reason: 'SAME_STATUS' };
  // ADR 0467 D1 — the job's side comes from the ONE lock table (closed: nothing · cancelled: only to cancelled · unknown: nothing)
  const lock = cargoTicketLock(ticketStatus);
  if (lock.status === 'none') return { ok: false, reason: lock.reason ?? 'TICKET_STATUS_UNKNOWN' };
  if (lock.status === 'only_cancelled' && to !== 'cancelled') return { ok: false, reason: lock.reason ?? 'TICKET_CANCELLED' };
  if (from === 'pending') return { ok: true };
  if (CARGO_OUTCOMES.includes(from)) return to === 'pending' || CARGO_OUTCOMES.includes(to) ? { ok: true } : { ok: false, reason: 'BAD_TRANSITION' };
  if (from === 'cancelled') return to === 'pending' ? { ok: true } : { ok: false, reason: 'BAD_TRANSITION' };
  return { ok: false, reason: 'BAD_TRANSITION' };
}

export interface CargoStatusNeed {
  qty_done: boolean;
  reason_code: boolean;
  reason_text: boolean;
  disposition: boolean;
  photo: boolean;
}

/**
 * ADR 0409 D3/D4 — "ตั้งสถานะนี้แล้วต้องกรอกอะไรอีก" · **คำตอบเดียวที่ทุกฟอร์มต้องถาม**
 *
 * `disposition` บังคับเมื่อ `deliver` + `failed` **หรือ `partial`** (ADR 0415 D2 — ส่วนที่ส่งไม่ได้ก็ต้องมีที่วาง)
 *   ของเคยขึ้นรถแล้วจึงมีที่อยู่ให้ตาม
 *   `pickup`/`return` ที่ `failed` คือยังไม่เคยขึ้นรถ ไม่มีอะไรให้ตาม
 *
 * `photo` บังคับเฉพาะ `via: 'driver'` ที่สถานะปลายทาง — **ไม่ใช่ทุกคนที่ตั้งสถานะ**
 *   เหตุผล: คนขับอยู่ตรงหน้าของ มีรูปให้ถ่าย · แอดมินแก้ย้อนหลังจากออฟฟิศไม่มีของให้ถ่าย
 *   บังคับทั้งสองฝั่ง = แอดมินแก้สถานะไม่ได้เลย ซึ่งพังของที่ใช้อยู่ (ADR 0409 rev 4)
 *   เกณฑ์ออกจากแบบนี้: ถ้ามี org ขอให้บังคับ/ผ่อนต่างจากนี้ → ทำเป็นค่าตั้งต่อองค์กรแบบ
 *   `fuel_station_radius_m` **ไม่ใช่**แก้ค่าคงที่ในโค้ด
 */
export function cargoStatusNeeds(status: CargoItemStatus, action: CargoAction, via: CargoStatusVia): CargoStatusNeed {
  const base = CARGO_STATUS_REQUIRED[status] ?? [];
  return {
    qty_done: base.includes('qty_done'),
    reason_code: base.includes('reason_code'),
    reason_text: base.includes('reason_text'),
    disposition: action === 'deliver' && (status === 'failed' || status === 'partial'),
    photo: via === 'driver' && CARGO_OUTCOMES.includes(status),
  };
}

/** ADR 0415 D1 — `return_to_stop` ต้องบอกว่าหมุดไหน · ขึ้นกับค่าที่ส่งมา จึงแยกจาก `cargoStatusNeeds`. */
export function dispositionNeedsStop(d?: string | null): boolean {
  return d === 'return_to_stop';
}

/** i18n key ของป้ายสถานะ — `cargo.status.deliver.done` = "ส่งแล้ว". */
export const cargoStatusKey = (action: string, status: string) => `cargo.status.${action}.${status}`;
/** i18n key ของป้ายปลายทาง — `cargo.disposition.return_to_stop` = "วางคืนที่หมุด". */
export const cargoDispositionKey = (d: string) => `cargo.disposition.${d}`;

/**
 * ADR 0454 R3 — "อื่น ๆ" ต้องระบุข้อความ · ใช้กับทุกช่องทาง (คนขับ · แอดมิน · bulk)
 * ขึ้นกับค่าที่เลือก จึงแยกจาก `cargoStatusNeeds` (แบบเดียวกับ `dispositionNeedsStop`)
 * เกณฑ์ออก: เหตุผลตัวที่สองที่ต้องพิมพ์ → ย้ายเป็นธง `metadata.requires_text` ใน master data `cargo_fail_reason` ไม่ใช่เพิ่มรหัสที่นี่
 */
export const CARGO_REASON_OTHER = 'other';
export function reasonNeedsText(code?: string | null): boolean {
  return code === CARGO_REASON_OTHER;
}

/**
 * ADR 0454 R4 (Q16) — ของทุกชิ้นมีจุดจบ: **ส่งได้ + คืน + หาย = ทั้งหมด**
 * `cargoRemaining` = ของที่ยังไม่ได้ส่ง (ขาส่งที่ partial / failed) · null = ไม่มีของเหลือให้ถาม
 * ADR 0456 R2 — first argument = the deliver leg's BASE (`deliverBase(qty, pickedQty(pickup))`), not the planned qty:
 * picked up 4 of 10 and delivered 3 → 1 left (not 7).
 */
export function cargoRemaining(qty: number, status: string, qtyDone?: number | null): number | null {
  if (status === 'failed') return qty;
  if (status === 'partial' && qtyDone != null) return qty - qtyDone;
  return null;
}

export type ReturnQtyCheck =
  | { ok: true; return_qty: number; lost_qty: number }
  | { ok: false; reason: 'RETURN_QTY_RANGE' | 'QTY_NOT_INTEGER' };

/**
 * ADR 0454 R4 — ตรวจ `return_qty` ที่ส่งมา · ไม่ส่ง = คืนทั้งหมด (แอปเก่า/แอดมินเดิม) · หาย = ส่วนต่าง (**คำนวณที่นี่เสมอ ไม่รับจาก client**)
 */
export function checkReturnQty(remaining: number, returnQty?: number | null): ReturnQtyCheck {
  if (returnQty == null) return { ok: true, return_qty: remaining, lost_qty: 0 };
  if (!Number.isInteger(returnQty)) return { ok: false, reason: 'QTY_NOT_INTEGER' };
  if (returnQty < 1 || returnQty > remaining) return { ok: false, reason: 'RETURN_QTY_RANGE' };
  return { ok: true, return_qty: returnQty, lost_qty: remaining - returnQty };
}

/**
 * ADR 0456 R1/R2 — what the pickup leg of a pair actually got onto the truck (the base of its deliver leg):
 * done → `qty_done ?? qty` · partial → `qty_done` · failed / cancelled → 0 · pending → null (not picked yet — Q5: the deliver
 * leg then uses the planned qty) · no pair (rows before ADR 0407) → null. ONE rule for the driver app, the admin web and the API
 * (`apps/api/src/modules/cargo/lib/cargo-item-status.ts` copy) — pinned by `cargo-item.cases.json` `picked`.
 * Used for: the deliver leg's "ส่งครบ" (Q1), its qty range, what is left to put back (`cargoRemaining(base, …)`), job counts (R3).
 * plan: 4-Archives/plan/tms/0456-driver-cargo-photos-picked-qty-return-tasks-fresh-data.md
 */
export function pickedQty(pickup?: { status: string; qty: number; qty_done?: number | null } | null): number | null {
  if (!pickup) return null;
  switch (pickup.status) {
    case 'done': return pickup.qty_done ?? pickup.qty;
    case 'partial': return pickup.qty_done ?? 0;
    case 'failed':
    case 'cancelled': return 0;
    default: return null;
  }
}

/** ADR 0456 R2 — the deliver leg's working qty: what was picked up, or the planned qty while unknown */
export function deliverBase(qty: number, picked?: number | null): number {
  return picked ?? qty;
}

/**
 * ADR 0459 — the two photo sets a cargo row carries (D2: a removed photo records which set it came from) and the cap of
 * an admin's removal reason (D4). API copy `apps/api/src/modules/cargo/lib/cargo-item-status.ts` — pinned by
 * `cargo-item.cases.json` `photo_sets` · `photo_remove_reason_max`.
 * plan: 4-Archives/plan/tms/0459-admin-status-change-keeps-driver-photos.md
 */
export const CARGO_PHOTO_SETS = ['photos', 'return_photos'] as const;
export type CargoPhotoSet = (typeof CARGO_PHOTO_SETS)[number];
export const CARGO_PHOTO_REMOVE_REASON_MAX = 500;

/**
 * ADR 0459 D6 — a put-back not yet done (`returned_at` null — e.g. the office moved it to another pin) needs at least one
 * NEW photo in that save: the photos kept from the old pin do not prove the goods reached this one.
 * One rule, read by the API (`setReturnPhotos` → 400 `PHOTO_REQUIRED`) and the driver's return form (save stays off).
 * Pinned by `cargo-item.cases.json` `return_needs_new_photo`.
 */
export function returnNeedsNewPhoto(item: { returned_at?: string | Date | null }, newPhotos: number): boolean {
  return !item.returned_at && newPhotos < 1;
}
